import Box from "@/common/components/Box";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { BoxesIcon, BriefcaseBusinessIcon, ChevronDownIcon, ChevronUpIcon, FileBoxIcon } from "lucide-react";
import { formatCurrency, formatDecimalUnit, formatDecimalWithGroupedUnit } from "@/common/utils/formatters";
import ResumeItem from "@/common/components/ResumeItem";
import Button from "@/common/components/Button";
import { useState } from "react";
import { CompanyGovernmentAllocationDistribution } from '@/features/governmentAllocations/types/governmentAllocationDistribution.type';
import GovernmentAllocationDistributionUpdateModal from '../../../components/GovernmentAllocationDistribution/UpdateDistributionModal';
import { GovernmentAllocationDistributionDeleteModal } from "@/features/governmentAllocations/pages/Show/components/DeleteDistributionModal";
import { GovernmentAllocationProductCategoryDistributionDeleteModal } from "@/features/governmentAllocations/pages/Show/components/DeleteProductCategoryDistributionModal";
import { GovernmentAllocationProductDistributionDeleteModal } from "@/features/governmentAllocations/pages/Show/components/DeleteProductDistributionModal";

interface GovernmentAllocationDistributionBranchItemProps {
  distribution: CompanyGovernmentAllocationDistribution;
  isConsumptionControlledByQuantity?: boolean;
}

const GovernmentAllocationDistributionBranchItem = ({
  distribution,
  isConsumptionControlledByQuantity,
}: GovernmentAllocationDistributionBranchItemProps) => {
  const [showProductCategoriesDistributions, setShowProductCategoriesDistributions] = useState(distribution.product_categories_distributions.length > 0);

  return (
    <Box className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <ImageWithPlaceholder
            placeholderIcon={BriefcaseBusinessIcon}
            className="size-8"
            iconClassName="size-5"
            url={distribution.branch?.general_information?.company_logo?.url}
          />

          <p>{distribution.branch?.display_name}</p>
        </div>

        <div className="flex items-center gap-8">
          <GovernmentAllocationDistributionDeleteModal distributionId={distribution.id} />

          <GovernmentAllocationDistributionUpdateModal
            distribution={distribution}
            isConsumptionControlledByQuantity={isConsumptionControlledByQuantity}
          />
        </div>
      </div>

      <div className="flex items-end justify-between">
        <div className="flex items-end gap-4">
          <ResumeItem
            label={isConsumptionControlledByQuantity ? "Quantidade limite" : "Limite total"}
            value={isConsumptionControlledByQuantity
              ? formatDecimalUnit(Number(distribution.total_quantity))
              : formatCurrency(Number(distribution.total_value))}
            valueClassName="text-md"
          />

          <ResumeItem
            label={isConsumptionControlledByQuantity ? "Quantidade consumida" : "Total consumido"}
            className="text-yellow-400"
            value={isConsumptionControlledByQuantity
              ? formatDecimalUnit(Number(distribution.consumed_quantity))
              : formatCurrency(Number(distribution.consumed_total))}
            valueClassName="text-md"
          />

          <ResumeItem
            label={isConsumptionControlledByQuantity ? "Quantidade disponível para uso" : "Total disponível para uso"}
            className="text-green-400"
            value={isConsumptionControlledByQuantity
              ? formatDecimalUnit(Number(distribution.remaining_quantity))
              : formatCurrency(Number(distribution.remaining_total))}
            valueClassName="text-md"
          />
        </div>

        {distribution.product_categories_distributions.length > 0 && (
          <Button
            variant="outline-error"
            className="italic font-light text-sm"
            onClick={() => setShowProductCategoriesDistributions(!showProductCategoriesDistributions)}
          >
            {showProductCategoriesDistributions ? 'Esconder serviços e produtos' : 'Mostrar serviços e produtos'}

            {showProductCategoriesDistributions ? <ChevronUpIcon strokeWidth={1.5} className="size-4" /> : <ChevronDownIcon strokeWidth={1.5} className="size-4" />}
          </Button>
        )}
      </div>

      {showProductCategoriesDistributions && (
        <div className="flex flex-col gap-4">
          {distribution.product_categories_distributions.map((productCategoryDistribution) => (
            <Box className="flex flex-col gap-3" key={productCategoryDistribution.id}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BoxesIcon strokeWidth={1} />

                  <p>{productCategoryDistribution.product_category.display_name}</p>
                </div>

                <GovernmentAllocationProductCategoryDistributionDeleteModal
                  productCategoryDistributionId={productCategoryDistribution.id}
                />
              </div>

              <div className="flex gap-4">
                <ResumeItem
                  label={isConsumptionControlledByQuantity ? "Quantidade limite" : "Valor limite"}
                  value={isConsumptionControlledByQuantity
                    ? formatDecimalWithGroupedUnit(
                      Number(productCategoryDistribution.total_quantity),
                      productCategoryDistribution.products_distributions,
                      (p) => p.product.unit_for_quantity,
                    )
                    : formatCurrency(Number(productCategoryDistribution.total_value))}
                  valueClassName="text-md"
                />

                <ResumeItem
                  label={isConsumptionControlledByQuantity ? "Quantidade consumida" : "Total consumido"}
                  value={isConsumptionControlledByQuantity
                    ? formatDecimalWithGroupedUnit(
                      Number(productCategoryDistribution.consumed_quantity),
                      productCategoryDistribution.products_distributions,
                      (p) => p.product.unit_for_quantity,
                    )
                    : formatCurrency(Number(productCategoryDistribution.consumed_total))}
                  valueClassName="text-md"
                  className="text-yellow-400"
                />

                <ResumeItem
                  label={isConsumptionControlledByQuantity ? "Quantidade disponível para uso" : "Total disponível para uso"}
                  value={isConsumptionControlledByQuantity
                    ? formatDecimalWithGroupedUnit(
                      Number(productCategoryDistribution.remaining_quantity),
                      productCategoryDistribution.products_distributions,
                      (p) => p.product.unit_for_quantity,
                    )
                    : formatCurrency(Number(productCategoryDistribution.remaining_total))}
                  valueClassName="text-md"
                  className="text-green-400"
                />
              </div>

              {productCategoryDistribution.products_distributions.map((productDistribution) => (
                <Box className="p-3" key={productDistribution.id}>
                  <div className="flex flex-col gap-3 border-s border-blue-800 p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileBoxIcon strokeWidth={1.5} />

                        <p>{productDistribution.product.display_name}</p>
                      </div>

                      <GovernmentAllocationProductDistributionDeleteModal
                        productDistributionId={productDistribution.id}
                      />
                    </div>

                    <div className="flex items-end gap-4">
                      <ResumeItem
                        label={isConsumptionControlledByQuantity ? "Quantidade limite" : "Valor limite"}
                        value={isConsumptionControlledByQuantity
                          ? formatDecimalUnit(Number(productDistribution.total_quantity), productDistribution.product.unit_for_quantity)
                          : formatCurrency(Number(productDistribution.total_value))}
                        valueClassName="text-md"
                      />

                      <ResumeItem
                        label={isConsumptionControlledByQuantity ? "Quantidade consumida" : "Total consumido"}
                        value={isConsumptionControlledByQuantity
                          ? formatDecimalUnit(Number(productDistribution.consumed_quantity), productDistribution.product.unit_for_quantity)
                          : formatCurrency(Number(productDistribution.consumed_total))}
                        valueClassName="text-md"
                        className="text-yellow-300"
                      />

                      <ResumeItem
                        label={isConsumptionControlledByQuantity ? "Quantidade disponível para uso" : "Total disponível para uso"}
                        value={isConsumptionControlledByQuantity
                          ? formatDecimalUnit(Number(productDistribution.remaining_quantity), productDistribution.product.unit_for_quantity)
                          : formatCurrency(Number(productDistribution.remaining_total))}
                        valueClassName="text-md"
                        className="text-green-300"
                      />
                    </div>
                  </div>
                </Box>
              ))}
            </Box>
          ))}
        </div>
      )}
    </Box>
  );
};

export default GovernmentAllocationDistributionBranchItem;